import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ExternalLink, Phone, Heart, Shield, AlertCircle, 
  Users, BookOpen, TestTube, Pill, MapPin, Globe, Search
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { APP_CONFIG } from '../config';

const Resources = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all'); // all, southafrica, global

  const resourceCategories = [
    {
      id: 'emergency',
      title: '🚨 Emergency & Crisis (South Africa)',
      icon: AlertCircle,
      color: '#FF3B30',
      resources: APP_CONFIG.globalResources.emergencySA,
      description: 'Immediate help - police, ambulance, crisis lines'
    },
    {
      id: 'substance-sa',
      title: '🇿🇦 Substance Use Support (SA)',
      icon: Shield,
      color: '#00E5FF',
      resources: APP_CONFIG.globalResources.substanceUseSA,
      description: 'Treatment, counseling, support groups in South Africa'
    },
    {
      id: 'mental-sa',
      title: '🧠 Mental Health (South Africa)',
      icon: Heart,
      color: '#FFD800',
      resources: APP_CONFIG.globalResources.mentalHealthSA,
      description: 'Depression, anxiety, trauma counseling'
    },
    {
      id: 'sexual-sa',
      title: '❤️ Sexual Health & GBV (SA)',
      icon: Users,
      color: '#FF0080',
      resources: APP_CONFIG.globalResources.sexualHealthSA,
      description: 'Rape crisis, GBV support, HIV/AIDS services'
    },
    {
      id: 'harm-reduction',
      title: '🌍 Harm Reduction (Global)',
      icon: Shield,
      color: '#4ECDC4',
      resources: APP_CONFIG.globalResources.harmReductionGlobal,
      description: 'Drug testing, safer use education, DanceSafe'
    },
    {
      id: 'substance-global',
      title: '🌐 Substance Support (International)',
      icon: Pill,
      color: '#9B59B6',
      resources: APP_CONFIG.globalResources.substanceUseGlobal,
      description: 'USA, UK, Australia, Canada resources'
    },
    {
      id: 'mental-global',
      title: '💚 Mental Health (International)',
      icon: Heart,
      color: '#3498DB',
      resources: APP_CONFIG.globalResources.mentalHealthGlobal,
      description: 'Crisis Text Line, Befrienders, global hotlines'
    },
    {
      id: 'testing',
      title: '🧪 Drug Testing & Safety',
      icon: TestTube,
      color: '#E67E22',
      resources: APP_CONFIG.globalResources.drugTesting,
      description: 'Test kits, reagents, substance identification'
    },
    {
      id: 'overdose',
      title: '💉 Overdose Prevention',
      icon: AlertCircle,
      color: '#E74C3C',
      resources: APP_CONFIG.globalResources.overdosePrevention,
      description: 'Naloxone access, Never Use Alone hotline'
    },
    {
      id: 'education',
      title: '📚 Education & Information',
      icon: BookOpen,
      color: '#1ABC9C',
      resources: APP_CONFIG.globalResources.education,
      description: 'Erowid, PsychonautWiki, drug policy'
    }
  ];

  const filteredCategories = resourceCategories.filter(category => {
    if (selectedRegion === 'southafrica') {
      return category.id.includes('-sa') || category.id === 'emergency';
    }
    if (selectedRegion === 'global') {
      return category.id.includes('-global') || ['harm-reduction', 'testing', 'overdose', 'education'].includes(category.id);
    }
    return true; // 'all' shows everything
  });

  return (
    <div className="min-h-screen bg-white pb-24">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8"
      >
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/')}
            className="mb-4 bg-white border-2 border-black rounded-2xl"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <h1 className="text-4xl md:text-5xl font-black text-ssdp-navy mb-3 uppercase" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            Help & Resources
          </h1>
          <p className="text-lg text-ssdp-navy/70 font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
            Comprehensive South African & international support services
          </p>
        </div>

        {/* Emergency Banner */}
        <motion.div
          whileHover={{ scale: 1.01 }}
          className="bg-[#FF3B30] border-4 border-ssdp-navy rounded-3xl p-6 mb-8 shadow-[6px_6px_0px_0px_rgba(30,58,89,1)]"
        >
          <div className="flex items-start gap-4">
            <AlertCircle className="w-12 h-12 text-white flex-shrink-0" strokeWidth={3} />
            <div>
              <h3 className="text-2xl font-black text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Emergency? Get Help Now
              </h3>
              <div className="space-y-1 text-white font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span className="font-bold">South Africa: 10111 or 112</span> (Police/Ambulance)
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span className="font-bold">Crisis Line: 0800 567 567</span> (24/7 Free)
                </div>
                <p className="text-white/90 text-sm mt-2">
                  If you or someone else is in immediate danger, call emergency services first.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Region Filter */}
        <div className="mb-8 flex gap-3">
          <Button
            onClick={() => setSelectedRegion('all')}
            className={`border-4 border-ssdp-navy rounded-2xl font-bold px-6 py-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all ${
              selectedRegion === 'all' ? 'bg-[#CCFF00] text-ssdp-navy' : 'bg-white text-ssdp-navy/70 hover:bg-gray-100'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            <Globe className="w-4 h-4 mr-2" />
            All Resources
          </Button>
          <Button
            onClick={() => setSelectedRegion('southafrica')}
            className={`border-4 border-ssdp-navy rounded-2xl font-bold px-6 py-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all ${
              selectedRegion === 'southafrica' ? 'bg-[#00E5FF] text-ssdp-navy' : 'bg-white text-ssdp-navy/70 hover:bg-gray-100'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            <MapPin className="w-4 h-4 mr-2" />
            South Africa Only
          </Button>
          <Button
            onClick={() => setSelectedRegion('global')}
            className={`border-4 border-ssdp-navy rounded-2xl font-bold px-6 py-3 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all ${
              selectedRegion === 'global' ? 'bg-[#FF0080] text-white' : 'bg-white text-ssdp-navy/70 hover:bg-gray-100'
            }`}
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            <Globe className="w-4 h-4 mr-2" />
            International
          </Button>
        </div>

        {/* Resource Categories */}
        <div className="space-y-6">
          {filteredCategories.map((category, index) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white border-4 border-ssdp-navy rounded-3xl p-6 shadow-[6px_6px_0px_0px_rgba(30,58,89,1)]"
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="p-3 rounded-2xl border-4 border-ssdp-navy"
                  style={{ backgroundColor: category.color }}
                >
                  <category.icon className="w-6 h-6 text-white" strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-xl font-black text-ssdp-navy" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {category.title}
                  </h3>
                  <p className="text-sm text-ssdp-navy/70" style={{ fontFamily: 'Figtree, sans-serif' }}>
                    {category.description}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {category.resources.map((resource, idx) => (
                  <div
                    key={idx}
                    className="bg-gray-50 border-2 border-black rounded-2xl p-4 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4 className="font-bold text-ssdp-navy mb-1" style={{ fontFamily: 'Figtree, sans-serif' }}>
                          {resource.name}
                        </h4>
                        {resource.description && (
                          <p className="text-sm text-ssdp-navy/70 mb-2" style={{ fontFamily: 'Figtree, sans-serif' }}>
                            {resource.description}
                          </p>
                        )}
                        {resource.phone && (
                          <a
                            href={resource.url || `tel:${resource.phone.replace(/\s/g, '')}`}
                            className="flex items-center gap-1 text-sm font-bold text-[#00E5FF] hover:underline"
                            style={{ fontFamily: 'Figtree, sans-serif' }}
                          >
                            <Phone className="w-4 h-4" />
                            {resource.phone}
                          </a>
                        )}
                        {resource.url && !resource.phone && (
                          <a
                            href={resource.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-sm font-bold text-[#00E5FF] hover:underline"
                            style={{ fontFamily: 'Figtree, sans-serif' }}
                          >
                            <ExternalLink className="w-4 h-4" />
                            Visit Website
                          </a>
                        )}
                        <span className="inline-block mt-2 text-xs font-bold bg-black text-white px-2 py-1 rounded-full">
                          {resource.region}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer Note */}
        <div className="mt-8 bg-ssdp-teal/20 border-4 border-[#FFD800] rounded-3xl p-6">
          <p className="text-ssdp-navy font-medium" style={{ fontFamily: 'Figtree, sans-serif' }}>
            <strong>Note:</strong> Pathways provides educational information only. These resources are independently operated organizations. 
            For medical emergencies, always call emergency services (10111/112 in South Africa). 
            Resources are updated regularly but availability may change - please verify contact details when possible.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Resources;
